/**
 * Hook for DG SuperApp Bridge functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { bridgeService } from '../services/bridge';
import type { InitializeMiniAppModel, Attachment, AudioRecordingResult, MicrophoneOptions, StartRecordingResult, StopRecordingResult } from '../types';

interface UseBridgeReturn {
  // Initialization state
  isReady: boolean;
  isInSuperApp: boolean;
  isEmbedded: boolean;
  initData: InitializeMiniAppModel | null;

  // User profile
  userProfile: { initData: string } | null;
  loadUserProfile: () => Promise<{ initData: string } | null>;

  // DeviceId extraction
  extractDeviceId: () => Promise<string | null>;

  // App bar control
  setAppBarStyle: (title?: string, titleColor?: string, bgColor?: string) => Promise<void>;
  setAppBarVisible: (visible: boolean) => Promise<void>;

  // Media capture
  capturePhoto: () => Promise<Attachment | null>;
  pickPhoto: () => Promise<Attachment | null>;
  selectFile: () => Promise<Attachment | null>;
  selectMultipleFiles: () => Promise<Attachment[]>;

  // Scanning
  scanQRCode: () => Promise<string | null>;

  // Biometrics
  canUseBiometrics: () => Promise<boolean>;
  authenticateWithBiometrics: () => Promise<boolean>;

  // Sharing
  shareFile: (url: string, type: 'image' | 'doc' | 'anyFile', ext: string, name: string) => Promise<boolean>;

  // Audio recording
  initializeAudioRecorder: () => Promise<boolean>;
  isAudioReady: boolean;
  startRecording: (options?: MicrophoneOptions) => Promise<StartRecordingResult>;
  stopRecording: (returnFormat?: 'base64' | 'buffer') => Promise<StopRecordingResult>;
  audioResultToAttachment: (result: AudioRecordingResult) => Attachment | null;

  // Navigation
  openExternalLink: (url: string) => Promise<void>;
  close: () => Promise<void>;
  closeWithConfirm: () => Promise<void>;

  // Utilities
  safeArea: { top: number; bottom: number; left: number; right: number };
  locale: string;
  hasFeature: (feature: string) => boolean;
}

export function useBridge(): UseBridgeReturn {
  const [isReady, setIsReady] = useState(false);
  const [initData, setInitData] = useState<InitializeMiniAppModel | null>(null);
  const [userProfile, setUserProfile] = useState<{ initData: string } | null>(null);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      try {
        const data = await bridgeService.initialize();
        setInitData(data);
        setIsReady(true);

        // Automatically load user profile if in SuperApp
        if (data?.isDGSuperApp) {
          const profile = await bridgeService.loadUserProfile();
          setUserProfile(profile);
        }
      } catch (error) {
        console.error('Bridge initialization failed:', error);
        setIsReady(true); // Still mark as ready to allow standalone mode
      }
    };

    init();
  }, []);

  const loadUserProfile = useCallback(async (): Promise<{ initData: string } | null> => {
    const profile = await bridgeService.loadUserProfile();
    setUserProfile(profile);
    return profile;
  }, []);

  const extractDeviceId = useCallback(async (): Promise<string | null> => {
    return bridgeService.extractDeviceId();
  }, []);

  const setAppBarStyle = useCallback(
    async (title?: string, titleColor?: string, bgColor?: string) => {
      await bridgeService.setAppBarStyle(title, titleColor, bgColor);
    },
    []
  );

  const setAppBarVisible = useCallback(async (visible: boolean) => {
    await bridgeService.setAppBarVisible(visible);
  }, []);

  const capturePhoto = useCallback(async (): Promise<Attachment | null> => {
    return bridgeService.capturePhoto();
  }, []);

  const pickPhoto = useCallback(async (): Promise<Attachment | null> => {
    return bridgeService.pickPhoto();
  }, []);

  const selectFile = useCallback(async (): Promise<Attachment | null> => {
    return bridgeService.selectFile();
  }, []);

  const selectMultipleFiles = useCallback(async (): Promise<Attachment[]> => {
    return bridgeService.selectMultipleFiles();
  }, []);

  const scanQRCode = useCallback(async (): Promise<string | null> => {
    return bridgeService.scanQRCode();
  }, []);

  const canUseBiometrics = useCallback(async (): Promise<boolean> => {
    return bridgeService.canUseBiometrics();
  }, []);

  const authenticateWithBiometrics = useCallback(async (): Promise<boolean> => {
    return bridgeService.authenticateWithBiometrics();
  }, []);

  const shareFile = useCallback(
    async (
      url: string,
      type: 'image' | 'doc' | 'anyFile',
      ext: string,
      name: string
    ): Promise<boolean> => {
      return bridgeService.shareFile(url, type, ext, name);
    },
    []
  );

  const [isAudioReady, setIsAudioReady] = useState(false);

  const initializeAudioRecorder = useCallback(async (): Promise<boolean> => {
    const success = await bridgeService.initializeAudioRecorder();
    setIsAudioReady(success);
    return success;
  }, []);

  const startRecording = useCallback(async (options?: MicrophoneOptions): Promise<StartRecordingResult> => {
    return bridgeService.startRecording(options);
  }, []);

  const stopRecording = useCallback(async (returnFormat: 'base64' | 'buffer' = 'base64'): Promise<StopRecordingResult> => {
    return bridgeService.stopRecording(returnFormat);
  }, []);

  const audioResultToAttachment = useCallback((result: AudioRecordingResult): Attachment | null => {
    return bridgeService.audioResultToAttachment(result);
  }, []);

  const openExternalLink = useCallback(async (url: string) => {
    await bridgeService.openExternalLink(url);
  }, []);

  const close = useCallback(async () => {
    await bridgeService.close();
  }, []);

  const closeWithConfirm = useCallback(async () => {
    await bridgeService.closeWithConfirm();
  }, []);

  const hasFeature = useCallback((feature: string): boolean => {
    return bridgeService.hasFeature(feature);
  }, []);

  return {
    isReady,
    isInSuperApp: bridgeService.isInSuperApp,
    isEmbedded: bridgeService.isEmbedded,
    initData,
    userProfile,
    loadUserProfile,
    extractDeviceId,
    setAppBarStyle,
    setAppBarVisible,
    capturePhoto,
    pickPhoto,
    selectFile,
    selectMultipleFiles,
    scanQRCode,
    canUseBiometrics,
    authenticateWithBiometrics,
    initializeAudioRecorder,
    isAudioReady,
    startRecording,
    stopRecording,
    audioResultToAttachment,
    shareFile,
    openExternalLink,
    close,
    closeWithConfirm,
    safeArea: bridgeService.safeArea,
    locale: bridgeService.locale,
    hasFeature,
  };
}
